import { PageHeaderLayout, RouteTabWithContent, TabContent } from "@homebound/beam";
import { useMemo } from "react";
import { Navigate } from "react-router-dom";
import { createPlanPackageDetailsOverviewUrl, createPlanPackagesUrl } from "src/RouteUrls";
import { Loading } from "src/components/Loading";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { useUserFeatureFlagsQuery } from "src/contexts/FeatureFlags/FeatureFlagContext.generated";
import { FeatureFlagType } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import {
  PlanPackagePageV2MetadataQuery,
  usePlanPackagePageV2MetadataQuery,
} from "src/routes/libraries/plan-package-v2/PlanPackagePageV2.generated";
import { OptionsTab } from "src/routes/libraries/plan-package-v2/components/OptionsTab";
import { OverviewTab } from "src/routes/libraries/plan-package-v2/components/OverviewTab";
import { PlanPackageHeaderActions } from "src/routes/libraries/plan-package-v2/components/PlanPackageHeaderActions";
import { ProgramDataTab } from "src/routes/libraries/plan-package-v2/components/ProgramDataTab";
import { PlanPackageDetailsParams } from "src/routes/routesDef";
import * as librariesPaths from "src/routing/domains/librariesPaths";
import { queryResult } from "src/utils";

export function PlanPackagePageV2() {
  const isPlanPackagesV2Enabled = useFeatureFlag(FeatureFlagType.PlanPackagesV2);
  const { loading: flagsLoading } = useUserFeatureFlagsQuery();
  const { planPackageId, planPackageVersionId } = useRouteParams<PlanPackageDetailsParams>();
  const query = usePlanPackagePageV2MetadataQuery({
    variables: { id: planPackageId, versionId: planPackageVersionId },
    skip: !isPlanPackagesV2Enabled,
  });
  if (flagsLoading) return <Loading />;
  if (!isPlanPackagesV2Enabled) {
    return <Navigate to={createPlanPackageDetailsOverviewUrl(planPackageId, planPackageVersionId)} replace />;
  }
  return queryResult(query, ({ planPackage }) => <DataView planPackage={planPackage} />);
}

type DataViewProps = { planPackage: PlanPackagePageV2MetadataQuery["planPackage"] };

function DataView({ planPackage }: DataViewProps) {
  const { planPackageId, planPackageVersionId } = useRouteParams<PlanPackageDetailsParams>();
  const tabs: RouteTabWithContent[] = useMemo(() => {
    const params = { planPackageId, planPackageVersionId };
    return [
      {
        name: "Overview",
        href: librariesPaths.planPackageOverviewRoute.url(params),
        path: [librariesPaths.planPackageRoute.path, librariesPaths.planPackageOverviewRoute.path],
        render: () => <OverviewTab planPackageId={planPackageId} versionId={planPackageVersionId} />,
      },
      {
        name: "Elevations",
        href: librariesPaths.planPackageElevationsRoute.url(params),
        path: librariesPaths.planPackageElevationsRoute.path,
        render: () => <></>,
      },
      {
        name: "Program Data",
        href: librariesPaths.planPackageProgramDataRoute.url(params),
        path: librariesPaths.planPackageProgramDataRoute.path,
        render: () => <ProgramDataTab planPackageId={planPackageId} versionId={planPackageVersionId} />,
      },
      {
        name: "Locations",
        href: librariesPaths.planPackageLocationsRoute.url(params),
        path: librariesPaths.planPackageLocationsRoute.path,
        render: () => <></>,
      },
      {
        name: "Options",
        href: librariesPaths.planPackageOptionsRoute.url(params),
        path: librariesPaths.planPackageOptionsRoute.path,
        render: () => <OptionsTab planPackageId={planPackageId} versionId={planPackageVersionId} />,
      },
      {
        name: "Takeoffs",
        // Takeoffs is still its own full page (SideNav + TakeoffsManagerProvider), so it can't render inside a tab yet
        href: librariesPaths.planTakeoffsRoute.url(params),
        path: librariesPaths.planTakeoffsRoute.path,
        render: () => <></>,
      },
      {
        name: "Checkout",
        href: librariesPaths.planPackageCheckoutRoute.url(params),
        path: librariesPaths.planPackageCheckoutRoute.path,
        render: () => <></>,
      },
      {
        name: "Documents",
        href: librariesPaths.planPackageDocumentsRoute.url(params),
        path: librariesPaths.planPackageDocumentsRoute.path,
        render: () => <></>,
      },
    ];
  }, [planPackageId, planPackageVersionId]);

  return (
    <PageHeaderLayout
      pageHeader={{
        title: planPackage.name,
        breadcrumbs: { breadcrumbs: { label: "Plan Library", href: createPlanPackagesUrl() } },
        tabs: { tabs },
        rightSlot: <PlanPackageHeaderActions planPackage={planPackage} />,
      }}
    >
      <TabContent tabs={tabs} />
    </PageHeaderLayout>
  );
}
